import React, { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { imageString } from '../../helper';
import {
  CircleContainer,
  ImageConstraintsText,
  ImageDropzoneContainer,
  ImageIconContainer,
  ImageUploadContainer,
  ImageUploadInstructionText,
  PlusIconWrapper,
  PreviewImage
} from '../ImageUploadWithPreviewV3';
import ImageIcon from '../icons/ImageIcon';
import PlusIcon from '../icons/PlusIcon';
import { Palette } from '../utils';
import { createNewImageItem } from './entityImageHelpers';
import {
  EntityCoverBadge,
  EntityImageRemoveBtn,
  EntityImagesGrid,
  EntityImagesSection,
  EntityImageThumb,
  EntityImageTile
} from './entityImagesGalleryStyles';

const DEFAULT_MAX_IMAGES = 10;
const DEFAULT_MAX_SIZE = 5242880;

function EntityImagesGallery({
  t,
  images,
  setImages,
  building,
  mapNewImage,
  dropzoneTitle,
  dropHereText,
  constraintsText,
  maxReachedText,
  coverLabel,
  maxImages = DEFAULT_MAX_IMAGES,
  maxSize = DEFAULT_MAX_SIZE
}) {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const buildingPhotoRaw =
    typeof building?.photo === 'string' && building.photo.trim() !== '' ? building.photo : '';
  const buildingLogoSrc = buildingPhotoRaw ? imageString(buildingPhotoRaw) : '';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/jpeg': [], 'image/png': [] },
    maxSize,
    disabled: images.length >= maxImages,
    onDrop: (acceptedFiles) => {
      setImages((prev) => {
        const remaining = maxImages - prev.length;
        if (remaining <= 0) return prev;
        const nextFiles = acceptedFiles.slice(0, remaining);
        const nextObjs = nextFiles.map((file) =>
          mapNewImage ? mapNewImage(file) : createNewImageItem(file)
        );
        return [...prev, ...nextObjs];
      });
    }
  });

  const handleRemove = (id) => {
    setImages((prev) => {
      const found = prev.find((x) => x.id === id);
      if (found?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(found.preview);
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (_e, index) => {
    dragOverItem.current = index;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from == null || to == null || from === to) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const copy = [...images];
    const [removed] = copy.splice(from, 1);
    copy.splice(to, 0, removed);
    setImages(copy);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <EntityImagesSection>
      <ImageDropzoneContainer style={{ marginTop: 0 }} {...getRootProps()}>
        <input {...getInputProps()} />
        <ImageUploadContainer gapY="24px" alignItems="center">
          <CircleContainer $hasImage={!!buildingLogoSrc} aria-hidden>
            {buildingLogoSrc ? (
              <PreviewImage src={buildingLogoSrc} alt="" draggable={false} />
            ) : (
              <ImageIconContainer>
                <ImageIcon />
                <PlusIconWrapper>
                  <PlusIcon />
                </PlusIconWrapper>
              </ImageIconContainer>
            )}
          </CircleContainer>
          <div>
            <ImageUploadInstructionText>
              {isDragActive ? dropHereText : dropzoneTitle}
            </ImageUploadInstructionText>
            <ImageConstraintsText>{constraintsText}</ImageConstraintsText>
            {images.length >= maxImages ? (
              <p
                style={{
                  fontSize: 12,
                  color: Palette.ORANGE_500,
                  marginTop: 8,
                  marginBottom: 0
                }}>
                {maxReachedText}
              </p>
            ) : null}
          </div>
        </ImageUploadContainer>
      </ImageDropzoneContainer>

      {images.length > 0 ? (
        <EntityImagesGrid>
          {images.map((img, index) => (
            <div
              key={img.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              style={{
                cursor: 'grab',
                width: '100%',
                minWidth: 0
              }}>
              <EntityImageTile>
                {index === 0 && coverLabel ? (
                  <EntityCoverBadge>{coverLabel}</EntityCoverBadge>
                ) : null}
                <EntityImageThumb src={img.preview} alt="" draggable={false} />
                <EntityImageRemoveBtn
                  type="button"
                  draggable={false}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleRemove(img.id);
                  }}
                  aria-label={t('Remove')}>
                  ×
                </EntityImageRemoveBtn>
              </EntityImageTile>
            </div>
          ))}
        </EntityImagesGrid>
      ) : null}
    </EntityImagesSection>
  );
}

export default EntityImagesGallery;
